import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
    ReviewResult,
    DamageIssueType,
    DeliveryIssueTypeInternational,
    DocumentationErrorType,
    DocumentationIssue,
    InvoiceIssueTypeInternational,
    ItemOrQuantityIssueTypeInternational,
    LargeInstrumentWarehouseIssueType,
    OrderEntryIssueTypeDomestic,
    OrderEntryIssueTypeInternational,
    RootCause,
    QualityIssueType,
    CountrySpecificLabelIssue
} from '@cod-monorepo/interfaces';

export class WorkflowValidators {
    public static requiredField(controls: string[], formGroup: AbstractControl): ValidationErrors | null {
        const errors: ValidationErrors = {};

        for (const key of controls) {
            const control = formGroup.get(key);
            if (!control || control.value === null || control.value === undefined) {
                errors[`${key}Required`] = true;
            }
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public static required(controls: string[]): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            return WorkflowValidators.requiredField(controls, formGroup);
        };
    }

    public static financialAdjustment(formGroup: AbstractControl): ValidationErrors | null {
        const financialAdjustment = formGroup.get('financialAdjustment');
        const filedWithin30Days = formGroup.get('filedWithin30Days');
        const adjustmentAmount = formGroup.get('adjustmentAmount');
        const managerApprovedRequest = formGroup.get('managerApprovedRequest');

        filedWithin30Days?.setErrors(null);
        adjustmentAmount?.setErrors(null);
        managerApprovedRequest?.setErrors(null);

        if (financialAdjustment?.value) {
            if (filedWithin30Days?.value === null || filedWithin30Days?.value === undefined) {
                return { filedWithin30DaysRequired: true };
            }

            if (filedWithin30Days.value === false && !managerApprovedRequest?.value) {
                managerApprovedRequest?.setErrors({ required: true });
                return { managerApprovedRequestRequired: true };
            }
        }

        if (
            adjustmentAmount?.value === null ||
            adjustmentAmount?.value === undefined ||
            adjustmentAmount?.value === ''
        ) {
            return { adjustmentAmountRequired: true };
        }

        return null;
    }

    public static orderEntryDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const issueType = formGroup.get('issueType')?.value;

            if (
                issueType === OrderEntryIssueTypeDomestic.WRONG_BILL_TO_SHIP_TO_ACCOUNT_DOM ||
                issueType === OrderEntryIssueTypeDomestic.WRONG_ACCOUNT_DOM
            ) {
                const accountErrorTypeControl = formGroup.get('accountErrorType');
                if (
                    !accountErrorTypeControl ||
                    accountErrorTypeControl.value === null ||
                    accountErrorTypeControl.value === undefined ||
                    !Array.isArray(accountErrorTypeControl.value) ||
                    accountErrorTypeControl.value.length === 0
                ) {
                    return { accountErrorTypeRequired: true };
                }
            }

            if (issueType === OrderEntryIssueTypeDomestic.WRONG_ORDER_TYPE_DOM) {
                const requiredOrderTypeControls = ['incorrectlyUsed', 'correctOrderType'];
                const errors = WorkflowValidators.requiredField(requiredOrderTypeControls, formGroup);

                if (errors) {
                    return errors;
                }
            }

            return WorkflowValidators.financialAdjustment(formGroup);
        };
    }

    public static orderEntryInternational(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const issueType = formGroup.get('issueType')?.value;
            const canIdentifyCartonId = formGroup.get('canIdentifyCartonId')?.value;
            const cartonIdControl = formGroup.get('cartonId');

            cartonIdControl?.setErrors(null);

            if (issueType === OrderEntryIssueTypeInternational.WRONG_ACCOUNT_INT) {
                const accountErrorTypeControl = formGroup.get('accountErrorType');
                if (
                    !accountErrorTypeControl ||
                    accountErrorTypeControl.value === null ||
                    accountErrorTypeControl.value === undefined
                ) {
                    return { accountErrorTypeRequired: true };
                }
            } else {
                const requiredControlsIntl = ['errorDescription', 'canIdentifyCartonId'];
                const errors = WorkflowValidators.requiredField(requiredControlsIntl, formGroup);

                if (errors) {
                    return errors;
                }
            }

            if (canIdentifyCartonId) {
                if (!cartonIdControl?.value) {
                    return { cartonIdRequired: true };
                }
            }

            return null;
        };
    }

    public static itemQuantityDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            return WorkflowValidators.financialAdjustment(formGroup);
        };
    }

    public static itemQuantityInternational(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const issueType = formGroup.get('issueType')?.value;
            const canIdentifyCartonId = formGroup.get('canIdentifyCartonId')?.value;
            const cartonIdControl = formGroup.get('cartonId');

            cartonIdControl?.setErrors(null);

            switch (issueType) {
                case ItemOrQuantityIssueTypeInternational.WRONG_BATCH_SHIPPED: {
                    const errors = WorkflowValidators.requiredField(['lotsDeclinedWithin24Hours'], formGroup);

                    if (errors) {
                        return errors;
                    }

                    if (formGroup.get('lotsDeclinedWithin24Hours')?.value) {
                        if (!formGroup.get('communicationDeclinedDocuments')?.value) {
                            return { communicationDeclinedDocumentsRequired: true };
                        }

                        if (!formGroup.get('financialAdjustment')?.value) {
                            return { financialAdjustmentRequired: true };
                        }
                    }
                    break;
                }
                case ItemOrQuantityIssueTypeInternational.WRONG_SMN_SHIPPED: {
                    const errors = WorkflowValidators.requiredField(['canIdentifyCartonId'], formGroup);

                    if (errors) {
                        return errors;
                    }

                    if (canIdentifyCartonId && !cartonIdControl?.value) {
                        return { cartonIdRequired: true };
                    }
                    break;
                }
                default: {
                    const errors = WorkflowValidators.requiredField(['canIdentifyCartonId'], formGroup);

                    if (errors) {
                        return errors;
                    }

                    if (canIdentifyCartonId && !cartonIdControl?.value) {
                        return { cartonIdRequired: true };
                    }

                    if (!formGroup.get('financialAdjustment')?.value) {
                        return { financialAdjustmentRequired: true };
                    }
                    break;
                }
            }

            return null;
        };
    }

    public static productDatingDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            return WorkflowValidators.financialAdjustment(formGroup);
        };
    }

    public static productDatingInternational(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            if (formGroup.get('declinedWithin24Hours')?.value) {
                const communicationDeclinedDocuments = formGroup.get('communicationDeclinedDocuments')?.value;
                if (!communicationDeclinedDocuments || communicationDeclinedDocuments.length < 1) {
                    return { communicationDeclinedDocumentsRequired: true };
                }

                if (!formGroup.get('financialAdjustment')?.value) {
                    return { financialAdjustmentRequired: true };
                }
            } else {
                const batchExpiryMeetsMinShipPolicy = formGroup.get('batchExpiryMeetsMinShipPolicy')?.value;

                if (batchExpiryMeetsMinShipPolicy === null || batchExpiryMeetsMinShipPolicy === undefined) {
                    return { batchExpiryMeetsMinShipPolicyRequired: true };
                }

                if (batchExpiryMeetsMinShipPolicy === false && !formGroup.get('financialAdjustment')?.value) {
                    return { financialAdjustmentRequired: true };
                }
            }

            return null;
        };
    }

    public static productDamageDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            switch (formGroup.get('damageType')?.value) {
                case DamageIssueType.APPARENT_OUTSIDE_PACKAGING:
                    if (formGroup.get('documentationNotedIssue')?.value === null) {
                        return { documentationNotedIssueRequired: true };
                    }

                    if (
                        formGroup.get('documentationNotedIssue')?.value === null &&
                        !formGroup.get('deliveryDocumentation')?.value
                    ) {
                        return { deliveryDocumentationRequired: true };
                    }
                    break;

                case DamageIssueType.CONCEALED_INTERNAL_PACKAGING: {
                    const internalControls = ['packageType', 'packageTypeCondition', 'additionalPackagingInfo'];
                    const errors = WorkflowValidators.requiredField(internalControls, formGroup);

                    if (errors) {
                        return errors;
                    }

                    if (formGroup.get('protectivePackagingUsed')?.value === null) {
                        return { protectivePackagingUsedRequired: true };
                    }

                    break;
                }
            }

            return WorkflowValidators.financialAdjustment(formGroup);
        };
    }

    public static productDamageInternational(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const canIdentifyCartonId = formGroup.get('canIdentifyCartonId')?.value;
            const cartonIdControl = formGroup.get('cartonId');
            const explanationOfMissingNoteControl = formGroup.get('explanationOfMissingNote');

            cartonIdControl?.setErrors(null);
            explanationOfMissingNoteControl?.setErrors(null);

            switch (formGroup.get('damageType')?.value) {
                case DamageIssueType.APPARENT_OUTSIDE_PACKAGING: {
                    const internalControls = ['documentationNotedIssue', 'damageClaimFiled', 'carrierTrackingNumber'];
                    const errors = WorkflowValidators.requiredField(internalControls, formGroup);

                    if (errors) {
                        return errors;
                    }

                    if (
                        formGroup.get('documentationNotedIssue')?.value === false &&
                        !explanationOfMissingNoteControl?.value
                    ) {
                        return { documentationNotedIssueRequired: true };
                    }

                    if (
                        !formGroup.get('deliveryDocumentation')?.value ||
                        !formGroup.get('deliveryDocumentation')?.value.length
                    ) {
                        return { deliveryDocumentationRequired: true };
                    }

                    if (
                        formGroup.get('damageClaimFiled')?.value &&
                        (!formGroup.get('damageClaimDocumentation')?.value ||
                            !formGroup.get('damageClaimDocumentation')?.value.length)
                    ) {
                        return { damageClaimDocumentationRequired: true };
                    }
                    break;
                }
                case DamageIssueType.CONCEALED_INTERNAL_PACKAGING: {
                    const internalControls = [
                        'packageType',
                        'protectivePackagingUsed',
                        'packageTypeCondition',
                        'additionalPackagingInfo',
                        'canIdentifyCartonId'
                    ];
                    const errors = WorkflowValidators.requiredField(internalControls, formGroup);

                    if (errors) {
                        return errors;
                    }

                    if (canIdentifyCartonId && !cartonIdControl?.value) {
                        return { cartonIdRequired: true };
                    }
                    break;
                }
            }

            return null;
        };
    }

    public static productTemperatureDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            return WorkflowValidators.financialAdjustment(formGroup);
        };
    }

    public static productTemperatureInternational(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            formGroup.get('explanationOfMissingNote')?.setErrors(null);
            formGroup.get('documentationNotedIssue')?.setErrors(null);

            if (formGroup.get('documentationNotedIssue')?.value === false) {
                if (!formGroup.get('explanationOfMissingNote')?.value) {
                    return { explanationOfMissingNoteRequired: true };
                }
            }

            if (formGroup.get('claimFiled')?.value) {
                if (!formGroup.get('carrierClaimDocumentation')?.value) {
                    return { carrierClaimDocumentationRequired: true };
                }
            }

            return null;
        };
    }

    public static deliveryErrorDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            if (formGroup.get('deliveryDocumentationMatchesAddress')?.value) {
                if (!formGroup.get('addressShippedTo')?.value) {
                    return { addressShippedToRequired: true };
                }
            }

            return WorkflowValidators.financialAdjustment(formGroup);
        };
    }

    public static deliveryErrorInternational(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const replacementOrderNumber = formGroup.get('replacementOrderNumber');

            replacementOrderNumber?.setErrors(null);

            switch (formGroup.get('issueType')?.value) {
                case DeliveryIssueTypeInternational.ATELLICA_INVENTORY_MANAGER:
                    if (
                        !formGroup.get('ainErrorType')?.value ||
                        formGroup.get('replacementOrderRequired')?.value === null
                    ) {
                        return {
                            ainErrorTypeRequired: true,
                            replacementOrderRequiredRequired: true
                        };
                    }

                    if (
                        formGroup.get('replacementOrderRequired')?.value &&
                        !formGroup.get('replacementOrderNumber')?.value
                    ) {
                        return { replacementOrderNumberRequired: true };
                    }
                    break;

                case DeliveryIssueTypeInternational.DELIVERED_BY_WRONG_CARRIER:
                    if (!formGroup.get('carrier')?.value || !formGroup.get('shipmentNumber')?.value) {
                        return {
                            carrierRequired: true,
                            shipmentNumberRequired: true
                        };
                    }
                    break;

                case DeliveryIssueTypeInternational.DELIVERED_TO_WRONG_ADDRESS: {
                    const requiredPortControls = [
                        'deliveryDocumentationMatchesAddress',
                        'addressShippedTo',
                        'shipmentNumber'
                    ];
                    const errors = WorkflowValidators.requiredField(requiredPortControls, formGroup);

                    if (errors) {
                        return errors;
                    }

                    break;
                }
                case DeliveryIssueTypeInternational.DELIVERED_TO_WRONG_PORT: {
                    const requiredPortControls = [
                        'deliveryDocumentationMatchesPort',
                        'portShippedTo',
                        'shipmentNumber'
                    ];
                    const errors = WorkflowValidators.requiredField(requiredPortControls, formGroup);

                    if (errors) {
                        return errors;
                    }

                    break;
                }
                case DeliveryIssueTypeInternational.EARLY_DELIVERY:
                case DeliveryIssueTypeInternational.LATE_DELIVERY:
                case DeliveryIssueTypeInternational.LOST_IN_TRANSIT:
                    if (!formGroup.get('shipmentNumber')?.value) {
                        return { shipmentNumberRequired: true };
                    }
                    break;
            }

            return null;
        };
    }

    public static documentationInternational(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            if (formGroup.get('documentationIssue')?.value === DocumentationIssue.OTHER) {
                if (!formGroup.get('documentationIssueOther')?.value) {
                    return { documentationIssueOtherRequired: true };
                }
            }

            if (formGroup.get('documentationErrorType')?.value === DocumentationErrorType.OTHER) {
                if (!formGroup.get('documentationErrorTypeOther')?.value) {
                    return { documentationErrorTypeOtherRequired: true };
                }
            }

            return null;
        };
    }

    public static invoiceErrorDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            return WorkflowValidators.financialAdjustment(formGroup);
        };
    }

    public static invoiceErrorInternational(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            if (formGroup.get('invoiceErrorType')?.value === InvoiceIssueTypeInternational.OTHER) {
                if (!formGroup.get('invoiceErrorTypeOtherInfo')?.value) {
                    return { invoiceErrorTypeOtherInfoRequired: true };
                }
            }

            if (formGroup.get('errorsIdentifiedWithin24hours')?.value) {
                if (
                    !formGroup.get('communicationDocuments')?.value ||
                    formGroup.get('communicationDocuments')?.value.length < 1
                ) {
                    return { communicationDocumentsRequired: true };
                }
            }

            if (formGroup.get('errorsIdentifiedWithin24hours')?.value === false) {
                const errors = WorkflowValidators.requiredField(['invoiceMatchesIODC'], formGroup);

                if (errors) {
                    return errors;
                }
            }

            return null;
        };
    }

    public static billingAdjustmentDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            return WorkflowValidators.financialAdjustment(formGroup);
        };
    }

    public static qualityInternational(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            switch (formGroup.get('issueType')?.value) {
                case QualityIssueType.COUNTRY_LABEL_ISSUE: {
                    if (formGroup.get('countrySpecificLabelMissing')?.value === CountrySpecificLabelIssue.INCORRECT) {
                        if (!formGroup.get('labelError')?.value) {
                            return { labelErrorRequired: true };
                        }
                    }
                    break;
                }
                case QualityIssueType.PRODUCT_LABEL: {
                    if (!formGroup.get('labelError')?.value) {
                        return { labelErrorRequired: true };
                    }
                    break;
                }
            }

            return null;
        };
    }

    public static eskerDiscrepancyDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            return WorkflowValidators.financialAdjustment(formGroup);
        };
    }

    public static largeInstrumentWarehouseDiscrepancy(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const canIdentifyCartonId = formGroup.get('canIdentifyCartonId')?.value;
            const cartonIdControl = formGroup.get('cartonId');

            cartonIdControl?.setErrors(null);

            switch (formGroup.get('issueType')?.value) {
                case LargeInstrumentWarehouseIssueType.QUALITY_REPLACEMENT_EXTERNAL_DAMAGE: {
                    const externalDamageControls = [
                        'documentationNotedIssue',
                        'additionalPackagingInfo',
                        'itemCondition'
                    ];
                    const errors = WorkflowValidators.requiredField(externalDamageControls, formGroup);

                    if (errors) {
                        return errors;
                    }

                    if (
                        formGroup.get('documentationNotedIssue')?.value &&
                        !formGroup.get('deliveryDocumentation')?.value
                    ) {
                        return { deliveryDocumentationRequired: true };
                    }
                    break;
                }
                case LargeInstrumentWarehouseIssueType.QUALITY_REPLACEMENT_INTERNAL_DAMAGE: {
                    const internalDamageControls = ['packageType', 'packageTypeCondition', 'additionalPackagingInfo'];
                    const errors = WorkflowValidators.requiredField(internalDamageControls, formGroup);

                    if (errors) {
                        return errors;
                    }

                    if (formGroup.get('protectivePackagingUsed')?.value === null) {
                        return { protectivePackagingUsedRequired: true };
                    }

                    break;
                }
            }

            if (canIdentifyCartonId && !cartonIdControl?.value) {
                return { cartonIdRequired: true };
            }

            return null;
        };
    }

    public static investigatorReview(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const investigatorReviewResult = formGroup.get('investigatorReviewResult')?.value;
            const reasonForDenial = formGroup.get('reasonForDenial')?.value;
            const natureOfComplaint = formGroup.get('natureOfComplaint')?.value;
            const issueType = formGroup.get('issueType')?.value;

            if (natureOfComplaint !== 'NO_CHANGE' && !issueType) {
                return { issueTypeRequired: true };
            }

            if (investigatorReviewResult === ReviewResult.DECLINED && !reasonForDenial) {
                return { reasonForDenialRequired: true };
            } else {
                formGroup.get('reasonForDenial')?.setErrors(null);
            }

            return null;
        };
    }

    public static investigatorReviewDomestic(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const investigatorReviewResult = formGroup.get('investigatorReviewResult')?.value;
            const reasonForDenial = formGroup.get('reasonForDenial')?.value;
            const natureOfComplaint = formGroup.get('natureOfComplaint')?.value;
            const issueType = formGroup.get('issueType')?.value;
            const rootCause = formGroup.get('rootCause')?.value;
            const csrResponsible = formGroup.get('csrResponsible')?.value;
            const through = formGroup.get('through')?.value;
            const nextStep = formGroup.get('nextStep')?.value;

            formGroup.get('through')?.setErrors(null);

            if (natureOfComplaint !== 'NO_CHANGE' && !issueType) {
                return { issueTypeRequired: true };
            }

            if (rootCause === RootCause.HUMAN_ERROR) {
                if (!csrResponsible) {
                    return { csrResponsibleRequired: true };
                }
            } else {
                if (!through) {
                    return { throughRequired: true };
                }
            }

            if (investigatorReviewResult === ReviewResult.APPROVED) {
                formGroup.get('reasonForDenial')?.setErrors(null);
                formGroup.get('nextStep')?.setErrors(null);
            }

            if (investigatorReviewResult === ReviewResult.DECLINED && !reasonForDenial) {
                return { reasonForDenialRequired: true };
            }

            if (investigatorReviewResult === ReviewResult.DECLINED && !nextStep) {
                return { nextStepRequired: true };
            }

            return null;
        };
    }

    public static managerReview(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const relatedManagerReview = formGroup.get('relatedManagerReview')?.value;
            const reasonForDenialControl = formGroup.get('reasonForDenial');
            const nextStepControl = formGroup.get('nextStep');

            if (relatedManagerReview === ReviewResult.DECLINED) {
                const requiredControls = ['reasonForDenial', 'nextStep'];
                const errors = WorkflowValidators.requiredField(requiredControls, formGroup);

                if (errors) {
                    return errors;
                }
            } else {
                reasonForDenialControl?.setErrors(null);
                nextStepControl?.setErrors(null);
            }

            return null;
        };
    }

    public static financeReview(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const financeCodReview = formGroup.get('financeCodReview')?.value;
            const reasonForDenialControl = formGroup.get('reasonForDenial');
            const nextStepControl = formGroup.get('nextStep');

            if (financeCodReview === ReviewResult.DECLINED) {
                const requiredControls = ['reasonForDenial', 'nextStep'];
                const errors = WorkflowValidators.requiredField(requiredControls, formGroup);

                if (errors) {
                    return errors;
                }
            } else {
                reasonForDenialControl?.setErrors(null);
                nextStepControl?.setErrors(null);
            }

            return null;
        };
    }
}
